﻿// src/components/ui/UI.jsx
import React, { useEffect, useState } from "react";
import { BRAND } from "../../config";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import PortalIcon from "./PortalIcon";
import useSiteContent from "../../hooks/useSiteContent";

export const GlobalCSS = ({ t }) => {
  useEffect(() => {
    document.body.style.background = t.bg;
    document.body.style.color = t.text;
    document.documentElement.style.background = t.bg;
    const root = document.getElementById("root");
    if (root) { root.style.background = t.bg; root.style.minHeight = "100vh"; }
  }, [t.bg, t.text]);
  return (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:smooth;}
    body{font-family:'Plus Jakarta Sans',sans-serif;background:${t.bg}!important;color:${t.text}!important;transition:background .3s,color .3s;overflow-x:hidden;}
    #root{background:${t.bg};min-height:100vh;transition:background .3s;}
    ::-webkit-scrollbar{width:5px;}::-webkit-scrollbar-thumb{background:${t.gold};border-radius:3px;}
    input,textarea,select{color:${t.text};}
    input::placeholder,textarea::placeholder{color:${t.muted}!important;}
    a{text-decoration:none;color:inherit;}
    button{font-family:inherit;cursor:pointer;}
    button:focus-visible,a:focus-visible,input:focus-visible,textarea:focus-visible,select:focus-visible{
      outline:2px solid ${t.gold};
      outline-offset:3px;
      box-shadow:0 0 0 5px ${t.gold}22;
    }
    img{max-width:100%;height:auto;}
    @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
    @keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
    @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
    
    /* Mobile-first responsive utilities */
    @media (max-width: 768px) {
      body { font-size: 14px; }
      h1 { font-size: clamp(1.5rem, 5vw, 2rem) !important; }
      h2 { font-size: clamp(1.25rem, 4vw, 1.5rem) !important; }
      h3 { font-size: clamp(1.1rem, 3.5vw, 1.25rem) !important; }
      .container { padding: 0 16px !important; }
    }
    
    /* Touch-friendly targets */
    @media (pointer: coarse) {
      button, a, input, select, textarea {
        min-height: 44px;
        min-width: 44px;
      }
    }
    
    /* Improved mobile interactions */
    @media (hover: none) {
      button:hover, a:hover {
        transform: none !important;
      }
    }
    
    /* Responsive grid system */
    .grid-responsive {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
      gap: clamp(12px, 3vw, 24px);
    }
    
    /* Mobile text overflow */
    @media (max-width: 640px) {
      .text-truncate-mobile {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
    @keyframes slideIn{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}
    @keyframes spin{to{transform:rotate(360deg)}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
    .fade-up{animation:fadeUp .6s ease both;}
    .floating{animation:float 4s ease-in-out infinite;}
    .card-hover{transition:transform .3s,box-shadow .3s!important;}
    .card-hover:hover{transform:translateY(-5px)!important;box-shadow:0 20px 50px ${t.shadow}!important;}
    .btn-hover{transition:transform .2s,opacity .2s!important;}
    .btn-hover:hover{transform:translateY(-2px)!important;opacity:.9!important;}
    .footer-link:hover{color:${t.gold}!important;transform:translateX(4px);}
    .line-clamp-2,.line-clamp-3{display:-webkit-box;-webkit-box-orient:vertical;overflow:hidden;}
    .line-clamp-2{-webkit-line-clamp:2;line-clamp:2;}
    .line-clamp-3{-webkit-line-clamp:3;line-clamp:3;}
    button,a{touch-action:manipulation;}
    .no-scrollbar{scrollbar-width:none;-ms-overflow-style:none;}
    .no-scrollbar::-webkit-scrollbar{display:none;}
    @keyframes bs-skeleton{from{opacity:.35}to{opacity:.75}}
    .bs-skeleton{animation:bs-skeleton 1.3s ease-in-out infinite alternate;}
    @supports(padding:env(safe-area-inset-bottom)){
      .safe-bottom-pad{padding-bottom:calc(10px + env(safe-area-inset-bottom))!important;}
    }
    @media(max-width:768px){
      .hide-mobile{display:none!important;}
      .stack-mobile{flex-direction:column!important;}
      .full-mobile{width:100%!important;}
      .center-mobile{text-align:center!important;}
      .grid-1-mobile{grid-template-columns:1fr!important;}
      .grid-2-mobile{grid-template-columns:1fr 1fr!important;}
      .pad-mobile{padding:60px 4%!important;}
      .hero-h{font-size:2rem!important;}
      .sec-h{font-size:1.6rem!important;}
      .nav-desktop{display:none!important;}
      .nav-mobile-btn{display:flex!important;}
      .footer-bottom-row{justify-content:center!important;text-align:center!important;}
      .footer-download-cta{width:100%!important;max-width:360px!important;justify-content:center!important;margin:4px auto!important;}
    }
    @media(min-width:769px){
      .hide-desktop{display:none!important;}
      .nav-mobile-btn{display:none!important;}
    }
    @media (hover:none){
      .card-hover:hover,.btn-hover:hover,.footer-link:hover{transform:none!important;}
    }
  `}</style>
  );
};

export const Tag = ({ children, t, color }) => (
  <span style={{
    display:"inline-block", padding:"5px 14px", borderRadius:50,
    fontSize:".72rem", fontWeight:700, letterSpacing:".1em", textTransform:"uppercase",
    marginBottom:12, background: color ? `${color}18` : t.accentBg,
    color: color || t.accent, border:`1px solid ${(color||t.accent)}30`,
  }}>{children}</span>
);

export const SecTitle = ({ tag, h, sub, center, t, tagColor }) => (
  <div style={{ textAlign:center?"center":"left", marginBottom:"clamp(28px,5vw,40px)" }}>
    {tag && <Tag t={t} color={tagColor}>{tag}</Tag>}
    <h2 className="sec-h" style={{
      fontFamily:"'Plus Jakarta Sans',sans-serif",
      fontSize:"clamp(1.75rem,3.2vw,2.55rem)", fontWeight:800,
      lineHeight:1.2, color:t.text, marginBottom:sub?10:0,
    }}>{h}</h2>
    {sub && <p style={{ fontSize:"clamp(.92rem,2vw,1rem)", color:t.muted, lineHeight:1.65, maxWidth:560, margin:center?"0 auto":0 }}>{sub}</p>}
  </div>
);

export const Btn = ({ children, variant="primary", href, onClick, style={}, t={} }) => {
  const base = {
    display:"inline-flex", alignItems:"center", gap:8, padding:"12px 24px",
    borderRadius:10, fontWeight:700, fontSize:".93rem", border:"none",
    fontFamily:"'Plus Jakarta Sans',sans-serif", textDecoration:"none", ...style,
  };
  const v = {
    primary:{ background:"linear-gradient(135deg,#F59E0B,#F97316)", color:"#070B14", boxShadow:"0 8px 24px rgba(245,158,11,.35)" },
    outline:{ background:"transparent", border:"2px solid rgba(255,255,255,.2)", color:"#fff" },
    green:  { background:"linear-gradient(135deg,#10B981,#059669)", color:"#fff" },
    wa:     { background:"#25D366", color:"#fff", boxShadow:"0 8px 24px rgba(37,211,102,.3)" },
    ghost:  { background:"transparent", border:`2px solid ${t.gold||"#F59E0B"}`, color:t.gold||"#F59E0B" },
    dark:   { background:t.bgCard||"#0F1629", color:t.text||"#F1F5F9", border:`1px solid ${t.border||"rgba(255,255,255,.1)"}` },
    danger: { background:"linear-gradient(135deg,#EF4444,#DC2626)", color:"#fff" },
    accent: { background:"linear-gradient(135deg,#6366F1,#4F46E5)", color:"#fff" },
  };
  const T = href ? "a" : "button";
  return <T href={href} onClick={onClick} className="btn-hover" style={{...base,...v[variant]}}>{children}</T>;
};

export const Card = ({ children, style={}, hover=true }) => (
  <div className={hover?"card-hover":""} style={{
    background:"var(--card,#0F1629)", border:"1px solid var(--bord,rgba(255,255,255,.07))",
    borderRadius:16, padding:"22px 20px", ...style,
  }}>{children}</div>
);

export const Spinner = ({ size=32, color="#F59E0B" }) => (
  <div style={{ width:size, height:size, border:`3px solid transparent`, borderTopColor:color,
    borderRadius:"50%", animation:"spin .8s linear infinite", display:"inline-block" }} />
);

const InlineIcon = ({ name, size = 15, style }) => (
  <PortalIcon name={name} size={size} style={{ marginRight:7, ...style }} />
);

export const GlowBg = ({ color, size=400, top, bottom, left, right, opacity=.15 }) => (
  <div style={{ position:"absolute", width:size, height:size, borderRadius:"50%",
    background:color, filter:"blur(100px)", opacity, top, bottom, left, right, pointerEvents:"none", zIndex:0 }} />
);

export const MarqueeStrip = ({ items: customItems }) => {
  const items = customItems || [
    "MCQ Banks","PPT Notes","Test Papers","Thumbnails",
    "Teacher Courses","Hindi Typing","Poster Design",
    "SEO Services","Digital Products","MSME Registered",
  ];
  const all = [...items,...items];
  return (
    <div style={{ background:"linear-gradient(90deg,#F59E0B,#F97316)", padding:"10px 0", overflow:"hidden" }}>
      <div style={{ display:"flex", animation:"marquee 38s linear infinite", whiteSpace:"nowrap" }}
        onMouseEnter={e=>e.currentTarget.style.animationPlayState="paused"}
        onMouseLeave={e=>e.currentTarget.style.animationPlayState="running"}>
        {all.map((item,i) => (
          <span key={i} style={{ padding:"0 26px", fontWeight:700, fontSize:".86rem", color:"#070B14", borderRight:"2px solid rgba(7,11,20,.18)" }}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

// Logo component â€” uses /images/logo.png or falls back to text
export const Logo = ({ size=38, showText=true, t={} }) => (
  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
    <div style={{ width:size, height:size, borderRadius:Math.round(size*.24), overflow:"hidden", flexShrink:0 }}>
      <img
        src="/images/logo.png"
        alt="DG HelpMate Logo"
        style={{ width:"100%", height:"100%", objectFit:"cover" }}
        onError={e => {
          const parent = e.currentTarget.parentElement;
          e.currentTarget.style.display = "none";
          parent.style.background = "linear-gradient(135deg,#F59E0B,#F97316)";
          parent.style.display = "flex";
          parent.style.alignItems = "center";
          parent.style.justifyContent = "center";
          parent.textContent = "DG";
          parent.style.fontWeight = "900";
          parent.style.fontSize = `${Math.round(size*.32)}px`;
          parent.style.color = "#070B14";
        }}
      />
    </div>
    {showText && (
      <span style={{ fontWeight:800, fontSize:`${Math.round(size*.34)}px`, color:t.text||"#F1F5F9" }}>
        DG <span style={{ color:t.gold||"#F59E0B" }}>HelpMate</span>
      </span>
    )}
  </div>
);

// Govardhan photo component
export const GovardhanPhoto = ({ size=200, style={} }) => (
  <div style={{
    width:size, height:size, borderRadius:"50%", overflow:"hidden",
    border:"4px solid #F59E0B", flexShrink:0,
    boxShadow:"0 20px 60px rgba(245,158,11,.25)", ...style,
  }}>
    <img
      src="/images/govardhan.jpg"
      alt="Govardhan Pandit"
      style={{ width:"100%", height:"100%", objectFit:"cover" }}
      onError={e => {
        const parent = e.currentTarget.parentElement;
        e.currentTarget.style.display = "none";
        parent.style.background = "linear-gradient(135deg,#6366F1,#F59E0B)";
        parent.style.display = "flex";
        parent.style.alignItems = "center";
        parent.style.justifyContent = "center";
        parent.textContent = "GP";
        parent.style.fontWeight = "900";
        parent.style.fontSize = `${Math.round(size*.28)}px`;
        parent.style.color = "#fff";
      }}
    />
  </div>
);

// Image Banner / Slider component â€” can be placed anywhere on website
export const ImageBanner = ({ slides=[], height=280, autoplay=true, interval=4000, style={} }) => {
  const [current, setCurrent] = React.useState(0);
  React.useEffect(() => {
    if (!autoplay || slides.length <= 1) return;
    const id = setInterval(() => setCurrent(c => (c+1) % slides.length), interval);
    return () => clearInterval(id);
  }, [slides.length, autoplay, interval]);
  if (!slides.length) return null;
  return (
    <div style={{ position:"relative", height, overflow:"hidden", ...style }}>
      {slides.map((slide, i) => (
        <div key={i} style={{
          position:"absolute", inset:0, transition:"opacity .8s",
          opacity: i === current ? 1 : 0,
        }}>
          <img src={slide.image} alt={slide.alt||""} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
          {slide.caption && (
            <div style={{
              position:"absolute", bottom:0, left:0, right:0, padding:"16px 24px",
              background:"linear-gradient(transparent,rgba(0,0,0,.6))",
              color:"#fff", fontWeight:700, fontSize:"1rem",
            }}>{slide.caption}</div>
          )}
        </div>
      ))}
      {slides.length > 1 && (
        <>
          <button onClick={() => setCurrent(c => (c-1+slides.length)%slides.length)}
            style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", background:"rgba(0,0,0,.4)", border:"none", color:"#fff", width:36, height:36, borderRadius:"50%", fontSize:"1.1rem" }}>â€¹</button>
          <button onClick={() => setCurrent(c => (c+1)%slides.length)}
            style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"rgba(0,0,0,.4)", border:"none", color:"#fff", width:36, height:36, borderRadius:"50%", fontSize:"1.1rem" }}>â€º</button>
          <div style={{ position:"absolute", bottom:10, left:"50%", transform:"translateX(-50%)", display:"flex", gap:6 }}>
            {slides.map((_,i) => (
              <button key={i} onClick={() => setCurrent(i)}
                style={{ width:i===current?20:8, height:8, borderRadius:4, border:"none", background:i===current?"#F59E0B":"rgba(255,255,255,.5)", transition:"all .3s" }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Public live chat widget (WhatsApp bubble + quick message popover)
export const WhatsAppFloat = ({ elevated = false } = {}) => {
  const [open, setOpen] = useState(false);
  const [msg, setMsg]   = useState("");
  const [name, setName] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const sendMsg = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      await addDoc(collection(db, "contactMessages"), {
        name: name || "Website Visitor",
        message: msg,
        source: "chat_widget",
        createdAt: serverTimestamp(),
      });
      setSent(true);
    } catch { setSent(true); }
    setSending(false);
  };

  return (
    <>
      {/* Popover */}
      {open && (
        <div style={{
          position:"fixed", bottom:elevated ? "calc(168px + env(safe-area-inset-bottom, 0px))" : 88, right:16, zIndex:1000,
          background:"#fff", borderRadius:18, boxShadow:"0 16px 64px rgba(0,0,0,.22)",
          width:300, overflow:"hidden", fontFamily:"'Plus Jakarta Sans',sans-serif",
        }}>
          {/* Header */}
          <div style={{ background:"#25D366", padding:"16px 18px 14px", color:"#fff" }}>
            <div style={{ fontWeight:800, fontSize:"1rem", marginBottom:3, display:"flex", alignItems:"center" }}><InlineIcon name="chat" size={16} style={{ color:"#fff" }} />Hi there!</div>
            <div style={{ fontSize:".82rem", opacity:.9 }}>
              How can we help you today? We usually reply within minutes.
            </div>
          </div>

          {sent ? (
            <div style={{ padding:"24px 18px", textAlign:"center" }}>
              <div style={{ width:46, height:46, borderRadius:14, background:"rgba(16,185,129,.12)", color:"#10B981", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 10px" }}><PortalIcon name="check" size={23} /></div>
              <div style={{ fontWeight:700, color:"#111", marginBottom:4, fontSize:".95rem" }}>Message Sent!</div>
              <div style={{ fontSize:".82rem", color:"#555" }}>We'll respond on WhatsApp shortly.</div>
              <button onClick={() => { setOpen(false); setSent(false); setMsg(""); setName(""); }}
                style={{ marginTop:12, padding:"8px 18px", background:"#25D366", color:"#fff", border:"none", borderRadius:8, fontWeight:700, cursor:"pointer", fontFamily:"inherit", fontSize:".85rem" }}>
                Close
              </button>
            </div>
          ) : (
            <div style={{ padding:"14px 16px" }}>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name (optional)"
                style={{ width:"100%", padding:"9px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:".85rem", color:"#111", fontFamily:"inherit", outline:"none", boxSizing:"border-box", marginBottom:8 }}/>
              <textarea value={msg} onChange={e=>setMsg(e.target.value)} rows={3}
                placeholder="Type your message..."
                style={{ width:"100%", padding:"9px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:".85rem", color:"#111", fontFamily:"inherit", outline:"none", resize:"none", boxSizing:"border-box", marginBottom:10 }}/>
              <button onClick={sendMsg} disabled={!msg.trim() || sending}
                style={{ width:"100%", padding:"10px", background:"#25D366", color:"#fff", border:"none", borderRadius:9, fontWeight:700, fontSize:".9rem", cursor:(!msg.trim()||sending)?"not-allowed":"pointer", fontFamily:"inherit", marginBottom:8, opacity:(!msg.trim()||sending)?.6:1 }}>
                {sending ? "Sending..." : <><InlineIcon name="chat" size={15} style={{ color:"#fff" }} />Send Message</>}
              </button>
              <a href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(msg||"Hi DG HelpMate!")}`}
                target="_blank"
                style={{ display:"block", textAlign:"center", padding:"9px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:9, color:"#16a34a", fontWeight:700, fontSize:".85rem", textDecoration:"none" }}>
                <InlineIcon name="phone" size={15} />Open in WhatsApp instead
              </a>
            </div>
          )}
        </div>
      )}

      {/* Bubble button */}
      <button onClick={() => setOpen(o=>!o)}
        style={{
          position:"fixed", bottom:elevated ? "calc(104px + env(safe-area-inset-bottom, 0px))" : 24, right:24, zIndex:1001,
          width:56, height:56, borderRadius:"50%",
          background:"#25D366", display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 8px 24px rgba(37,211,102,.5)", border:"none", cursor:"pointer",
          animation: open ? "none" : "pulse 2s ease-in-out infinite",
          transition:"transform .2s",
          transform: open ? "rotate(45deg) scale(1.1)" : "none",
        }}>
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
        ) : (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        )}
      </button>
    </>
  );
};

export const Footer = ({ setPage, t }) => {
  const { content } = useSiteContent();
  const dynGeneral  = content?.general  || {};
  const dynFooter   = content?.footer   || {};

  const supportPhone = dynGeneral.phone  || "+91 92416 53369";
  const supportEmail = dynGeneral.email  || "support@dghelpmate.com";
  const supportAddr  = dynGeneral.address || "Arwal, Bihar";
  const footerDesc   = dynFooter.description  || "Education content, digital services, and course tools for modern teachers and coaching institutes.";
  const copyrightTxt = dynFooter.copyrightText || "Proudly made for Indian teachers. © 2026 DG HelpMate.";

  const go = (page) => {
    if (page === "app") window.open("/app.html", "_blank");
    else {
      setPage(page);
      window.scrollTo?.(0, 0);
    }
  };
  const linkStyle = {
    display:"block",
    background:"none",
    border:"none",
    padding:"0 0 8px",
    color:t.muted,
    cursor:"pointer",
    fontSize:".84rem",
    textAlign:"left",
    fontWeight:600,
  };
  const downloadAppStyle = {
    display:"flex",
    alignItems:"center",
    gap:10,
    textAlign:"left",
    padding:"10px 12px",
    margin:0,
    borderRadius:14,
    border:`1px solid ${t.gold}55`,
    background:"linear-gradient(135deg,rgba(245,158,11,.16),rgba(249,115,22,.08))",
    color:t.text,
    cursor:"pointer",
    boxShadow:"0 12px 28px rgba(245,158,11,.12)",
    fontFamily:"'Plus Jakarta Sans',sans-serif",
  };
  const groups = [
    { head:"Store", links:[["All Store","store"],["Services","services"],["Bundles","bundles"],["Materials","materials"],["Branding Store","branding-store"]] },
    { head:"Company", links:[["About","about"],["Courses","courses"],["Blog","blog"],["Contact","contact"],["Search","search"]] },
    { head:"Legal", links:[["Privacy Policy","privacy"],["Terms","terms"],["Refund Policy","refund"],["Digital Delivery","delivery"],["Support","support"]] },
  ];

  return (
    <footer style={{ background:t.bgCard, borderTop:`1px solid ${t.border}`, padding:"38px 5% 18px" }}>
      <div style={{ maxWidth:1180, margin:"0 auto" }}>
        <div className="grid-1-mobile" style={{ display:"grid", gridTemplateColumns:"1.35fr repeat(3, .8fr) 1.05fr", gap:26, alignItems:"start", marginBottom:24 }}>
          <div>
            <button onClick={() => go("home")} style={{ background:"none", border:"none", padding:0, marginBottom:10, cursor:"pointer" }} aria-label="Go to home">
              <Logo t={t} size={36} />
            </button>
            <p style={{ fontSize:".84rem", color:t.muted, lineHeight:1.65, maxWidth:260, marginBottom:12 }}>
              {footerDesc}
            </p>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              <span style={{ display:"inline-flex", alignItems:"center", background:"rgba(16,185,129,.12)", color:t.green, padding:"6px 10px", borderRadius:999, fontSize:".75rem", fontWeight:800 }}>
                <InlineIcon name="check" size={13} />MSME Registered
              </span>
              <span style={{ display:"inline-flex", alignItems:"center", background:`${t.gold}12`, color:t.gold, padding:"6px 10px", borderRadius:999, fontSize:".75rem", fontWeight:800 }}>
                Est. 2025
              </span>
            </div>
          </div>

          {groups.map(({ head, links }) => (
            <div key={head}>
              <div style={{ fontSize:".72rem", fontWeight:900, letterSpacing:".12em", textTransform:"uppercase", color:t.gold, marginBottom:12 }}>{head}</div>
              {links.map(([label, page]) => (
                <button className="footer-link" key={`${head}-${label}`} onClick={() => go(page)} style={{...linkStyle, transition:"color .2s ease, transform .2s ease"}}>
                  {label}
                </button>
              ))}
            </div>
          ))}

          <div>
            <div style={{ fontSize:".72rem", fontWeight:900, letterSpacing:".12em", textTransform:"uppercase", color:t.gold, marginBottom:12 }}>Contact</div>
            {[
              ["phone", supportPhone],
              ["mail",  supportEmail],
              ["info",  supportAddr],
            ].map(([icon,val]) => (
              <div key={val} style={{ fontSize:".82rem", color:t.muted, marginBottom:9, display:"flex", alignItems:"center", gap:8, lineHeight:1.35 }}>
                <PortalIcon name={icon} size={14}/>
                <span>{val}</span>
              </div>
            ))}
            <a href={`https://wa.me/${dynGeneral.whatsapp || BRAND.whatsapp}`} target="_blank" rel="noreferrer"
              style={{ marginTop:12, display:"inline-flex", alignItems:"center", gap:7, background:"rgba(37,211,102,.10)", color:"#25D366", padding:"9px 13px", borderRadius:12, border:"1px solid rgba(37,211,102,.28)", fontWeight:800, fontSize:".82rem", textDecoration:"none" }}>
              <PortalIcon name="chat" size={15} />WhatsApp Support
            </a>
          </div>
        </div>

        <div className="footer-bottom-row" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:16, borderTop:`1px solid ${t.border}`, flexWrap:"wrap", gap:14 }}>
          <p style={{ fontSize:".78rem", color:t.muted }}>{copyrightTxt}</p>
          <button onClick={() => go("app")} className="btn-hover footer-download-cta" style={downloadAppStyle} aria-label="Download DG HelpMate app">
            <span style={{
              width:34,
              height:34,
              borderRadius:10,
              background:"linear-gradient(135deg,#F59E0B,#F97316)",
              color:"#070B14",
              display:"flex",
              alignItems:"center",
              justifyContent:"center",
              boxShadow:"0 8px 18px rgba(245,158,11,.28)",
              flexShrink:0,
            }}>
              <PortalIcon name="download" size={17} stroke={2.4} />
            </span>
            <span style={{ display:"flex", flexDirection:"column", gap:2, minWidth:0 }}>
              <span style={{ color:t.text, fontWeight:900, fontSize:".86rem", lineHeight:1.15 }}>Download App</span>
              <span style={{ color:t.gold, fontWeight:800, fontSize:".68rem", lineHeight:1.2 }}>Quick access</span>
            </span>
          </button>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center", fontSize:".78rem", color:t.muted }}>
            <span>dghelpmate.com</span>
            <span style={{ width:4, height:4, borderRadius:"50%", background:t.border }} />
            <span>All rights reserved</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

